import { memo, useState } from 'react';
import Link from 'next/link';
import { motion } from 'framer-motion';
import LockIcon from '@mui/icons-material/Lock';
import MilitaryTechIcon from '@mui/icons-material/MilitaryTech';
import BlockIcon from '@mui/icons-material/Block';
import StarsIcon from '@mui/icons-material/Stars';
import CardGiftcardIcon from '@mui/icons-material/CardGiftcard';
import { DropsTabStatus } from 'root/src/client/v2/_pages/drops/fixtures/drops.constants';
import { routeUrls } from 'root/src/shared/descriptions/routes/routeUrls';
import { buildAssetUrl } from 'root/src/client/v2/common/helpers/assetUrl';
import defaultPublisherLogo from 'root/src/client/public/default-publisher-logo.png';
import { DarePlatformIcon, PlatformIconColor } from 'root/src/client/web/base/CustomButton/PlatformIcon';
import { GamePlatformIcon } from 'root/src/client/v2/common/components/Games/components/GamePlatformIcon';
import { providersLabelMap } from 'root/src/shared/constants/providers';
import { readablePlatformsMap } from 'root/src/shared/constants/dropKeys';
import { useIsClipReviewer } from 'root/src/client/v2/auth/hooks/useIsClipReviewer';
import { usePointsEligibility } from 'root/src/client/v2/drop/hooks/usePointsEligibility';
import { Tooltip, TooltipTrigger, TooltipContent } from 'root/src/client/v2/common/components/ui/tooltip';
import { CardImageGallery } from 'root/src/client/v2/_pages/drops/redesign/CardImageGallery';
import { upscaleBoxArt } from 'root/src/client/v2/_pages/drops/redesign/dropImage';
import { cn } from 'root/src/client/v2/common/helpers/cn';
import { DROP_TAB_STATUS, getDropCta, getPointsIndicator } from 'root/src/client/v2/_pages/drops/redesign/dropStatus';
import { Img } from 'root/src/client/v2/common/components/ui/image';
const capitalize = (value) => value.charAt(0).toUpperCase() + value.slice(1);
const Hint = ({ label, children, contentClassName, onOpenChange }) => (<Tooltip onOpenChange={onOpenChange}>
		<TooltipTrigger asChild>{children}</TooltipTrigger>
		<TooltipContent className={contentClassName}>{label}</TooltipContent>
	</Tooltip>);
const chipClass = 'flex h-7 w-7 items-center justify-center rounded-md backdrop-blur-md shadow-sm';
const DetailRow = ({ label, value }) => (<div className="flex gap-2">
		<dt className="w-[4.5rem] shrink-0 text-muted-foreground/60">{label}</dt>
		<dd className="flex-1 text-muted-foreground">{value}</dd>
	</div>);
export const RedesignDropCard = memo(({ drop, status, index }) => {
    var _a, _b, _c, _d, _e;
    const { isClipReviewer } = useIsClipReviewer();
    const { showPointsUi } = usePointsEligibility(drop);
    const statusConfig = (_a = DROP_TAB_STATUS[status]) !== null && _a !== void 0 ? _a : DROP_TAB_STATUS[DropsTabStatus.Live];
    const cta = getDropCta(drop);
    const points = getPointsIndicator(drop, showPointsUi);
    const isGoodMatch = (_b = drop.dropUserInfo) === null || _b === void 0 ? void 0 : _b.isGoodMatch;
    const showExtraDares = isClipReviewer && Boolean(drop.extraDaresEnabled);
    const [hovering, setHovering] = useState(false);
    const rawImages = drop.images && drop.images.length > 0 ? drop.images : [drop.image];
    const galleryImages = rawImages
        .map(src => buildAssetUrl(upscaleBoxArt(src, '600x800'), { w: 720, q: 80, fmt: 'webp' }))
        .filter((src) => Boolean(src));
    const brandImage = (_c = buildAssetUrl(drop.brand.img, { w: 160, q: 80, fmt: 'webp' })) !== null && _c !== void 0 ? _c : defaultPublisherLogo.src;
    // Everything below already ships in the list payload; the card just cannot fit it. Icon-only rows get
    // spelled out (readablePlatformsMap, since capitalize turns `epicGamesStore` into `EpicGamesStore`),
    // and the dare allowance is otherwise only visible indirectly, once the CTA flips to "maxed".
    const streamOn = drop.platforms.map(platform => { var _a; return (_a = providersLabelMap[platform]) !== null && _a !== void 0 ? _a : capitalize(platform); }).join(', ');
    const playOn = drop.gamePlatforms.map(platform => { var _a; return (_a = readablePlatformsMap[platform]) !== null && _a !== void 0 ? _a : capitalize(platform); }).join(', ');
    const userInfo = drop.dropUserInfo;
    const daresAllowance = userInfo && userInfo.maxDares > 0
        ? `${userInfo.countOfAcceptedDares} of ${userInfo.maxDares} accepted`
        : undefined;
    const lockReason = ((_d = drop.lockStatus) === null || _d === void 0 ? void 0 : _d.isLocked) ? drop.lockStatus.description : undefined;
    // Skipped entirely when there is nothing to add beyond the title, so the panel never just echoes the card.
    const hasDetails = Boolean(drop.description || drop.genres.length || streamOn || playOn || daresAllowance || lockReason);
    // Opening is driven by hover on the WHOLE card (the same state the image gallery runs on) while the
    // trigger stays on the title block purely as the anchor, which keeps Link/<a> untouched. A counter,
    // not a boolean: chips sit next to each other, so moving between two of them fires the new one's open
    // before the old one's close, and a boolean would flicker the panel back on in between.
    const [openHints, setOpenHints] = useState(0);
    const onHintOpenChange = (open) => setOpenHints(count => Math.max(0, count + (open ? 1 : -1)));
    const showDetails = hasDetails && hovering && openHints === 0;
    const titleBlock = (<div className="mt-2">
			<h3 className="line-clamp-1 font-display text-lg font-bold text-foreground">{drop.label}</h3>
			<p className="mt-1 line-clamp-2 text-sm text-muted-foreground">{drop.description}</p>
		</div>);
    return (<motion.div layout initial={{ opacity: 0, y: 20, scale: 0.95 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, scale: 0.95 }} transition={{ duration: 0.3, delay: Math.min(index * 0.05, 0.3), layout: { duration: 0.3 } }} 
    // `min-w-0` pairs with the grid's base `grid-cols-1`: the track can shrink, but a grid item
    // still defaults to `min-width: auto` (its min-content), so without this the card itself keeps
    // the column wider than the viewport on mobile. Both halves are needed.
    className="h-full min-w-0">
			<Link href={routeUrls.drop(drop.mnemonicId)} passHref>
				<a onMouseEnter={() => setHovering(true)} onMouseLeave={() => setHovering(false)} className="group flex h-full flex-col overflow-hidden rounded-xl border border-solid border-white/[0.06] bg-card no-underline transition-colors hover:bg-card/80">
					<div className="relative aspect-video overflow-hidden">
						<CardImageGallery images={galleryImages} alt={drop.label} hovering={hovering}/>
						<div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-card via-transparent to-transparent opacity-50"/>

						<div className="absolute left-3 top-3">
							<span className={cn('inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-semibold shadow-sm backdrop-blur-md', statusConfig.badge)}>
								<span className={cn('h-1.5 w-1.5 rounded-full', statusConfig.dot)}/>
								{statusConfig.label}
							</span>
						</div>

						<div className="absolute right-3 top-3 flex items-center gap-1.5">
							{points.show && (<Hint label={points.message} onOpenChange={onHintOpenChange}>
									<span className={cn(chipClass, points.className)}><StarsIcon sx={{ fontSize: 15 }}/></span>
								</Hint>)}
							{showExtraDares && (<Hint label="This Drop awards Clip Reviewers with extra Dares!" onOpenChange={onHintOpenChange}>
									<span className={cn(chipClass, 'border border-solid border-neon-purple/40 bg-black/60 text-neon-purple')}><CardGiftcardIcon sx={{ fontSize: 15 }}/></span>
								</Hint>)}
							{((_e = drop.lockStatus) === null || _e === void 0 ? void 0 : _e.isLocked) && (<Hint label={drop.lockStatus.description || 'This drop is locked'} contentClassName="max-w-[220px] text-center leading-relaxed" onOpenChange={onHintOpenChange}>
									<span className={cn(chipClass, 'border border-solid border-white/25 bg-black/60 text-foreground')}><LockIcon sx={{ fontSize: 15 }}/></span>
								</Hint>)}
						</div>

						{isGoodMatch === true && (<Hint label="This drop is a great fit for your content!" onOpenChange={onHintOpenChange}>
								<span className="absolute bottom-3 left-3 inline-flex items-center gap-1 rounded-md border border-solid border-neon-green/40 bg-black/60 px-2.5 py-1.5 text-[11px] font-bold uppercase text-neon-green shadow-sm backdrop-blur-md">
									<MilitaryTechIcon sx={{ fontSize: 15 }}/>
									Best Fit
								</span>
							</Hint>)}
						{isGoodMatch === false && (<Hint label="This drop is for creators with a strong focus on this genre; your content is not a close enough fit." contentClassName="max-w-[220px] text-center leading-relaxed" onOpenChange={onHintOpenChange}>
								<span className="absolute bottom-3 left-3 inline-flex items-center gap-1 rounded-md border border-solid border-neon-pink/40 bg-black/60 px-2.5 py-1.5 text-[11px] font-bold uppercase text-neon-pink shadow-sm backdrop-blur-md">
									<BlockIcon sx={{ fontSize: 15 }}/>
									Low Fit
								</span>
							</Hint>)}
					</div>

					<div className="flex flex-1 flex-col p-5">
						<div className="flex items-center gap-2">
							<Img src={brandImage} alt={drop.brand.name} className="h-5 w-5 shrink-0 rounded-full border border-solid border-white/10 object-cover"/>
							<span className="truncate text-xs text-muted-foreground">{drop.brand.name}</span>
						</div>

						{hasDetails ? (<Tooltip open={showDetails}>
								<TooltipTrigger asChild>{titleBlock}</TooltipTrigger>
								<TooltipContent side="right" align="start" className="max-w-[22rem] p-3">
									<p className="font-display text-sm font-semibold text-foreground">{drop.label}</p>
									{drop.description && (<p className="mt-1.5 whitespace-pre-line text-xs leading-relaxed text-muted-foreground">{drop.description}</p>)}
									<dl className="mt-2.5 space-y-1.5 border-t border-solid border-white/[0.06] pt-2.5 text-[11px]">
										{drop.genres.length > 0 && <DetailRow label="Genres" value={drop.genres.join(', ')}/>}
										{streamOn && <DetailRow label="Stream on" value={streamOn}/>}
										{playOn && <DetailRow label="Play on" value={playOn}/>}
										{daresAllowance && <DetailRow label="Your dares" value={daresAllowance}/>}
									</dl>
									{lockReason && (<p className="mt-2.5 flex gap-1.5 rounded-md bg-white/[0.04] p-2 text-[11px] leading-relaxed text-muted-foreground">
											<LockIcon sx={{ fontSize: 13 }} className="mt-px shrink-0"/>
											{lockReason}
										</p>)}
								</TooltipContent>
							</Tooltip>) : titleBlock}

						{(drop.platforms.length > 0 || drop.gamePlatforms.length > 0) && (<div className="mt-3 flex items-center gap-2.5">
								{drop.platforms.length > 0 && (<ul className="flex items-center gap-1.5">
										{drop.platforms.map(platform => {
                    var _a;
                    return (<li key={platform}>
												<Hint label={(_a = providersLabelMap[platform]) !== null && _a !== void 0 ? _a : capitalize(platform)} onOpenChange={onHintOpenChange}>
													<span className="flex h-5 w-5 items-center justify-center rounded-full bg-white">
														<DarePlatformIcon size="12" platform={platform} color={PlatformIconColor.Dark}/>
													</span>
												</Hint>
											</li>);
                })}
									</ul>)}
								{drop.platforms.length > 0 && drop.gamePlatforms.length > 0 && (<span className="h-3.5 w-px bg-white/10"/>)}
								{drop.gamePlatforms.length > 0 && (<ul className="flex items-center gap-2 text-muted-foreground">
										{drop.gamePlatforms.map(platform => (<li key={platform}>
												<Hint label={capitalize(platform)} onOpenChange={onHintOpenChange}>
													<span className="flex items-center text-[15px] leading-none">
														<GamePlatformIcon platform={platform}/>
													</span>
												</Hint>
											</li>))}
									</ul>)}
							</div>)}

						{drop.genres.length > 0 && (<p className="mt-2 truncate text-xs text-muted-foreground/70">{drop.genres.join(', ')}</p>)}

						<div className="mt-auto pt-4">
							{cta.kind === 'action' ? (<span className="block w-full rounded-lg bg-gradient-brand py-2 text-center text-xs font-semibold text-white transition-opacity group-hover:opacity-90">
									{cta.label}
								</span>) : (<span className="flex w-full items-start justify-center gap-2 rounded-lg bg-secondary px-3 py-2.5 text-center text-xs font-medium leading-relaxed text-muted-foreground">
									<LockIcon sx={{ fontSize: 13 }} className="mt-0.5 shrink-0"/>
									{cta.label}
								</span>)}
						</div>
					</div>
				</a>
			</Link>
		</motion.div>);
});
